import React from "react"
import autobind from "class-autobind"
import Table from "../../../components/common/Table"
import { CircularProgress, TableHeader, TableRow, TableHeaderColumn, TableBody, TableRowColumn } from "material-ui"
import strings from "../../../localization/strings"
import * as api from "../../../api"
import { EOTEmployeeType, IDate, IOTEmployee, IOTEmployeeSearch } from "../../../models"
import AccountStore from "../../../stores/AccountStore"
import ImageLoader from "../../../components/misc/ImageLoader"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import * as timeHelper from "../../../helpers/timeHelper"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import { browserHistory } from "react-router"
import * as userHelper from "../../../helpers/userHelper"
import ExternalLink from "../../../components/common/externalLink/ExternalLink"
import SupervisorsToolTipColumn from "../../../components/supervisor/SupervisorsToolTipColumn"
import DashboardOvertimeDetailReports from "./DashboardOvertimeDetailReports"

interface IProps {
    utcTime: IDate
    globalHierarchyItemId: string
    filterByPunchPermissionHierarchy: boolean
    filterDirectReportsOnly: boolean
    type: EOTEmployeeType
}
interface IState {
    loading: boolean
    offset: number
    limit: number
    total: number
    overtimeEmployees: IOTEmployee[]
}

export default class DashboardOvertimeDetail extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: true,
            offset: 0,
            limit: 25,
            total: 0,
            overtimeEmployees: []
        }
        autobind(this)
    }

    componentDidMount() {
        this.getData(this.props)
    }

    componentWillReceiveProps(nextProps: IProps) {
        (
            this.props.globalHierarchyItemId !== nextProps.globalHierarchyItemId ||
            this.props.utcTime !== nextProps.utcTime ||
            this.props.filterByPunchPermissionHierarchy !== nextProps.filterByPunchPermissionHierarchy ||
            this.props.filterDirectReportsOnly !== nextProps.filterDirectReportsOnly
        ) &&
            this.setState({
                overtimeEmployees: [],
                offset: 0
            }, () =>
                this.getData(nextProps)
            )
    }

    getData(props: IProps) {
        if (!userHelper.getSupervisorAccessPermissionForHierarchyId(props.globalHierarchyItemId))
            return

        this.setState({ loading: true }, () =>
            api.getDashboardOTEmployees(props.type, props.globalHierarchyItemId, props.utcTime, props.filterDirectReportsOnly, props.filterByPunchPermissionHierarchy, this.state.offset, this.state.limit).then(res => {
                const result = res.data as IOTEmployeeSearch
                this.setState({
                    loading: false,
                    total: result.TotalCount,
                    overtimeEmployees: result.Employees
                })
            })
        )
    }

    onClickEmployeeCol(userId) {
        permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.ViewEmployees) &&
            permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ViewTimecards) &&
            browserHistory.push("/user-details/" + userId + "/timecards")
    }

    render() {
        return (
            <div style={{ padding: 15 }}>
                <div style={{ margin: 10, display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 20, fontWeight: 500 }}>
                        {this.props.type === EOTEmployeeType.ApproachingOT ? strings.Approaching_Overtime : strings.Overtime}
                    </div>
                    <DashboardOvertimeDetailReports
                        type={this.props.type}
                        utcTime={this.props.utcTime}
                        globalHierarchyItemId={this.props.globalHierarchyItemId}
                        filterByPunchPermissionHierarchy={this.props.filterByPunchPermissionHierarchy}
                        filterDirectReportsOnly={this.props.filterDirectReportsOnly}
                    />
                </div>
                {this.state.loading ?
                    <div className="spinner">
                        <CircularProgress />
                    </div> :
                    this.state.overtimeEmployees.length === 0 ?
                        <div>
                            <h2 style={{ textAlign: "center", marginTop: 25 }}>
                                {this.props.type === EOTEmployeeType.ApproachingOT ?
                                    strings.No_approaching_overtime_employee_found :
                                    strings.No_overtime_employee_found
                                }
                            </h2>
                        </div> :
                        <Table
                            readonly
                            items={this.state.overtimeEmployees}
                            total={this.state.total}
                            offset={this.state.offset}
                            limit={this.state.limit}
                            limitOptions={[25, 50, 100]}
                            onPageClick={offset => this.setState({ offset }, () => this.getData(this.props))}
                            onLimitChange={limit => this.setState({ limit, offset: 0 }, () => this.getData(this.props))}
                        >
                            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                <TableRow>
                                    <TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 15 }} />
                                    <TableHeaderColumn style={styles.imageColumnHeader} />
                                    <TableHeaderColumn style={styles.columnHeader}>{strings.Name}</TableHeaderColumn>
                                    <TableHeaderColumn style={styles.columnHeader}>{strings.Reg_Hours}</TableHeaderColumn>
                                    <TableHeaderColumn style={styles.columnHeader}>{strings.OT_Hours}</TableHeaderColumn>
                                    {!AccountStore.getHideOT2() &&
                                        <TableHeaderColumn style={styles.columnHeader}>{strings.OT2_Hours}</TableHeaderColumn>
                                    }
                                    <TableHeaderColumn style={styles.columnHeader}>{strings.Open_Active_Hours}</TableHeaderColumn>
                                    {this.props.type === EOTEmployeeType.ApproachingOT &&
                                        <TableHeaderColumn style={styles.columnHeader}>{strings.Hours_Until_OT}</TableHeaderColumn>
                                    }
                                    <TableHeaderColumn style={styles.columnHeader}>
                                        {this.props.filterByPunchPermissionHierarchy ? strings.Punch_Permission_Hierarchy : strings.Default_Hierarchy}
                                    </TableHeaderColumn>
                                    {AccountStore.getSupervisorDirectReportEnabled() &&
                                        <TableHeaderColumn style={styles.columnHeader}>{strings.Supervisor}</TableHeaderColumn>
                                    }
                                </TableRow>
                            </TableHeader>
                            <TableBody displayRowCheckbox={false} deselectOnClickaway={false}>
                                {this.state.overtimeEmployees.map((row, i) =>
                                    <TableRow key={i}>
                                        <TableRowColumn style={{ width: 25, paddingLeft: 5, paddingRight: 15, paddingTop: 4 }}>
                                            <ExternalLink
                                                url={"/user-details/" + row.Id + "/" + userHelper.getValidSubPages()[0]}
                                            />
                                        </TableRowColumn>
                                        <TableRowColumn style={styles.imageColumn}>
                                            {(row.ProfileImageID || row.ProfileImageS3Url) &&
                                                <div onClick={() => this.onClickEmployeeCol(row.Id)}>
                                                    <ImageLoader url={row.ProfileImageS3Url} fixedHeight={75} avatar={true} src={"/users/profileimage/" + row.ProfileImageID} key={`smallImg-${i}`} />
                                                </div>
                                            }
                                        </TableRowColumn>
                                        <TableRowColumn style={styles.columnValue}>
                                            <div onClick={() => this.onClickEmployeeCol(row.Id)} style={{ fontWeight: "bold" }}>
                                                {row.UserName}
                                                <br></br>
                                                <span style={{ color: "rgb(158, 158, 158)", fontSize: 10 }}>Emp #: {row.UserExternalId}</span>
                                            </div>
                                        </TableRowColumn>
                                        <TableRowColumn style={styles.columnValue}>{timeHelper.convertToHour(row.Reg)}</TableRowColumn>
                                        <TableRowColumn style={styles.columnValue}>{timeHelper.convertToHour(row.OT)}</TableRowColumn>
                                        {!AccountStore.getHideOT2() &&
                                            <TableRowColumn style={styles.columnValue}>{timeHelper.convertToHour(row.OT2)}</TableRowColumn>
                                        }
                                        <TableRowColumn style={styles.columnValue}>{timeHelper.convertToHour(row.EstimatedTotal - row.TotalProductive)}</TableRowColumn>
                                        {this.props.type === EOTEmployeeType.ApproachingOT &&
                                            <TableRowColumn style={styles.columnValue}>{timeHelper.convertToHour(40 * 60 - row.EstimatedTotal)}</TableRowColumn>
                                        }
                                        <TableRowColumn style={styles.columnValue} title={hierarchyHelper.getFullPathLabel(this.props.filterByPunchPermissionHierarchy ? row.DefaultHierarchyItemId : row.DefaultPunchHierarchyId)}>
                                            {hierarchyHelper.getFullyQualifiedLevelNameById(this.props.filterByPunchPermissionHierarchy ? row.DefaultHierarchyItemId : row.DefaultPunchHierarchyId)}
                                        </TableRowColumn>
                                        {AccountStore.getSupervisorDirectReportEnabled() &&
                                            <TableRowColumn style={styles.columnValue}>
                                                <SupervisorsToolTipColumn
                                                    toolTipKey={row.Id}
                                                    supervisorIds={row.SupervisorIds}
                                                />
                                            </TableRowColumn>
                                        }
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                }
            </div>
        )
    }
}

const styles = {
    columnValue: {
        textAlign: "left",
        cursor: "pointer",
        paddingLeft: 5,
        paddingRight: 5
    },
    columnHeader: {
        textAlign: "left",
        paddingLeft: 5,
        paddingRight: 5
    },
    imageColumnHeader: {
        width: 40,
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5,
    },
    imageColumn: {
        width: 40,
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5,
        cursor: "pointer"
    }
}